import { faSync } from "@fortawesome/pro-solid-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { createDeviceEvent, pollCheckDevice } from "../../actions/Devices/actions";
import { DeviceEventType } from "../../actions/Devices/constants";
import { FormFtaDeviceField } from "../../actions/Tenants/config/constantsTyped";
import Plot from "../../plotly/Plot";
import { RootState } from "../../reducers";
import { callAvosApi } from "../../utils/useAvosApiHook";
// import { listenForTriggerDeviceEvents } from "../Devices/TriggerDeviceButton";
import useConfig from "../../actions/Tenants/config/configHook";
import { Button } from "../Helper/Button";

import { useRouteCheck, useRouteLayer } from "../../hooks/useCurrentEntities";

export interface FtaDeviceFieldProps {
    item: FormFtaDeviceField;
    object: any;
    setValue: (name: string, value: any) => void;
    disabled: boolean;
}

interface FieldProps {
    type: "text" | "number" | "checkbox";
    value: any;
    onChange: (value: any) => void;
    disabled: boolean;
}

const InputField = ({ type, value, onChange, disabled }: FieldProps) => {
    return (
        <Input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
            disabled={disabled}
        />
    );
};

const CheckboxField = ({ type, value, onChange, disabled }: FieldProps) => {
    return (
        <Input
            type={type}
            checked={value ?? false}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
        />
    );
};

const getFieldComponent = (type: "text" | "number" | "checkbox") => {
    const fieldMap = {
        text: InputField,
        number: InputField,
        checkbox: CheckboxField,
    };
    return fieldMap[type] || InputField;
};

const checkTypeMismatch = (value: any, type: FormFtaDeviceField["data_value_type"]) => {
    if (value === undefined) return false;
    if (type === "text") return typeof value !== "string";
    if (type === "number") return typeof value !== "number";
    if (type === "boolean") return typeof value !== "boolean";
    return false;
};

interface ParsedMeasurement {
    distance: number[];
    loadcell: number[];
    pressure: number | null;
}

function parseFtaMeasurementData(raw: string): ParsedMeasurement {
    if (!raw || raw.length < 3) {
        return { distance: [], loadcell: [], pressure: null };
    }
    if (raw.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed.pos_arr) && Array.isArray(parsed.force_arr)) {
                return {
                    distance: parsed.pos_arr.map(Number),
                    loadcell: parsed.force_arr.map(Number),
                    pressure: typeof parsed.pressure === "number" ? parsed.pressure : null,
                };
            }
        } catch {
            // fall through
        }
    }
    const distance: number[] = [];
    const loadcell: number[] = [];
    let pressure: number | null = null;
    const cleaned = raw.replace(/#/g, "").replace(/\n/g, "").replace(/\r/g, "").trim();
    const parts = cleaned.split("l");
    if (parts.length > 0 && parts[parts.length - 1].includes("S")) {
        const lastPart = parts.pop()!;
        const pressureStr = lastPart.split("S")[0];
        const parsedPressure = parseFloat(pressureStr);
        if (!Number.isNaN(parsedPressure)) pressure = parsedPressure;
    }
    parts.filter((p) => p.trim() !== "").forEach((part) => {
        const values = part.split(",");
        if (values.length !== 2) return;
        const d = parseFloat(values[0]);
        const l = parseFloat(values[1]);
        if (Number.isNaN(d) || Number.isNaN(l)) return;
        distance.push(d);
        loadcell.push(l);
    });
    return { distance, loadcell, pressure };
}

const getInputTypeFromDataType = (type: any): "number" | "checkbox" | "text" => {
    if (type === "string") return "text";
    if (type === "number") return "number";
    if (type === "boolean") return "checkbox";
    return "text";
};

export const listenForEventsProcessed = (device_uid: string | undefined, check: any | undefined, setTriggerInProgress: (value: boolean) => void, shouldPoll: boolean) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const processed = useSelector<RootState, any>((state) => state.devices.processed);
    const use_fta_device = check?.use_fta_device && device_uid && check?.check_id && shouldPoll;

    useEffect(() => {
        let interval;
        if (use_fta_device) {
            dispatch(pollCheckDevice(device_uid, check.check_id));
            interval = setInterval(() => {
                dispatch(pollCheckDevice(device_uid, check.check_id));
            }, 750);
        }
        return interval !== undefined ? () => clearInterval(interval) : undefined;
    }, [use_fta_device, device_uid, check?.check_id, dispatch]);

    useEffect(() => {
        if (use_fta_device && processed.length > 0) {
            // Only reload if we were actually polling
            queryClient.invalidateQueries({ queryKey: ["check", check.tenant_id, check.check_id] });
            setTriggerInProgress(false);
        }
    }, [processed.length, use_fta_device, check?.check_id, check?.tenant_id, dispatch, queryClient]);
};


export function FtaDeviceField({ item, object, setValue, disabled }: FtaDeviceFieldProps) {
    const value = object[item.data_key];
    const dispatch = useDispatch();
    const check = useRouteCheck();
    const layer = useRouteLayer();
    const selected_fta = useSelector<RootState, any>((state) => state.devices.selected_fta);
    const config = useConfig(); // show_advanced_fta_check_page

    const location_config = config.get_check_type_config(layer, { location: check.location }); // show_advanced_fta_check_page
    const show_advanced_fta_check_page = location_config?.show_advanced_fta_check_page;
    const device_id: string | undefined = selected_fta?.UID;
    // Maintain trigger states for each avocado_id
    const [ftaTriggerStates, setFtaTriggerStates] = useState<{ [key: string]: boolean }>({});
    const [triggerInProgress, setTriggerInProgress] = useState(false);
    const [parsedMeasurement, setParsedMeasurement] = useState<ParsedMeasurement | null>(null);

    const fetchMeasurement = useCallback(async () => {
        if (!object.avocado_id) return;
        try {
            const response = await callAvosApi(`/avocados/${object.avocado_id}/measurement/latest/`);
            if (response.data) {
                const parsed = parseFtaMeasurementData(response.data.data);
                setParsedMeasurement(parsed.distance.length > 0 ? parsed : null);
            } else {
                setParsedMeasurement(null);
            }
        } catch {
            // silently ignore
        }
    }, [object.avocado_id]);

    // Fetch measurement for this fruit when the fruit changes or after a measurement is processed (value updated)
    useEffect(() => {
        if (!show_advanced_fta_check_page) return;
        fetchMeasurement();
    }, [show_advanced_fta_check_page, fetchMeasurement, value]);

    const plotData = useMemo(() => {
        if (!parsedMeasurement || parsedMeasurement.distance.length === 0) return null;
        return [{
            x: parsedMeasurement.distance,
            y: parsedMeasurement.loadcell,
            type: "scatter" as const,
            mode: "lines" as const,
            name: "Load Cell (gram)",
            line: { color: "#1f77b4", width: 2 },
        }];
    }, [parsedMeasurement]);

    const plotLayout = useMemo(() => {
        const pressureText = parsedMeasurement?.pressure != null
            ? ` | Pressure: ${parsedMeasurement.pressure.toFixed(3)} kg`
            : "";
        return {
            title: `FTA Measurement${pressureText}`,
            xaxis: { title: "Distance (steps)" },
            yaxis: { title: "Loadcell (g)" },
            autosize: true,
            margin: { l: 50, r: 20, t: 40, b: 40 },
            height: 250,
        };
    }, [parsedMeasurement]);

    // Only poll when we're actually waiting for a measurement
    const shouldPoll = triggerInProgress || value === undefined || value === null || value === "";
    listenForEventsProcessed(device_id, check, setTriggerInProgress, shouldPoll);

    let type = getInputTypeFromDataType(item.data_value_type);
    const isThereDataTypeMismatch = checkTypeMismatch(value, item.data_value_type);

    if (isThereDataTypeMismatch) {
        disabled = true;
        type = getInputTypeFromDataType(typeof value);
    }

    useEffect(() => {
        const avocadoId = object.avocado_id;
        // Check for both undefined and null, and also empty string or 0
        const shouldTrigger = value === undefined || value === null || value === "";
        if (check?.use_fta_device && device_id && !ftaTriggerStates[avocadoId] && shouldTrigger) {
            dispatch(createDeviceEvent({
                check_test_id: check.check_id,
                check_fruit_id: avocadoId,
                device_id,
                command: DeviceEventType.TRIGGER,
            }));
            setFtaTriggerStates((prevState) => ({
                ...prevState,
                [avocadoId]: true, // Mark this fruit as triggered
            }));
            setTriggerInProgress(true);
        }
    }, [value, object.avocado_id, check?.use_fta_device, device_id, check?.check_id, dispatch]);

    const FieldComponent = getFieldComponent(type);
    const onTriggerClick = () => {
        setValue(item.data_key, undefined);
        setFtaTriggerStates((prevState) => ({
            ...prevState,
            [object.avocado_id]: false, // Mark this fruit as not triggered
        }));
    };

    const formGroupContent = (
        <FormGroup>
            <Label for={item.data_key}>{item.label}</Label>
            <div className="d-flex align-items-center">
                <FieldComponent
                    type={type}
                    value={value}
                    onChange={(newValue) => setValue(item.data_key, newValue)}
                    disabled={disabled}
                />
                <Button
                    color={triggerInProgress ? "secondary" : "primary"}
                    onClick={onTriggerClick}
                    className="ms-2"
                    disabled={triggerInProgress}
                    icon={faSync}
                />
            </div>
            {isThereDataTypeMismatch && (
                <small className="text-danger">
                    {`Type mismatch. Expected ${item.data_value_type} but got ${typeof value}`}
                </small>
            )}
        </FormGroup>
    );

    if (show_advanced_fta_check_page) {
        return (
            <Row className="align-items-start g-2">
                <Col xs={4} md={3}>
                    {formGroupContent}
                </Col>
                <Col xs={8} md={9}>
                    {plotData
                        ? <Plot data={plotData} layout={plotLayout} style={{ width: "100%" }} useResizeHandler />
                        : <small className="text-muted">No measurement data yet.</small>
                    }
                </Col>
            </Row>
        );
    }

    return (
        <>
            {formGroupContent}
        </>
    );
}

